import { useEffect, useMemo, useState } from "react";

interface BlogRendererProps {
  content: string | object;
  className?: string;
}

// The renderer itself (tiptap + prosemirror + DOMPurify, ~700 KB minified)
// lives in ./blog-html and is fetched on first use, so it stays out of the
// entry chunk every public page loads.
type BlogHtml = typeof import("./blog-html");

let loaded: BlogHtml | null = null;
let loading: Promise<BlogHtml> | null = null;

const RELOAD_KEY = "blog-renderer-reload";

function loadBlogHtml(): Promise<BlogHtml> {
  loading ??= import("./blog-html").then(
    (mod) => {
      loaded = mod;
      try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* storage blocked */ }
      return mod;
    },
    (err) => {
      loading = null;
      // A failed chunk import after a deploy means this tab runs an old build
      // whose chunks are gone; reload once to pick up the new one.
      try {
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
        }
      } catch { /* storage blocked */ }
      throw err;
    },
  );
  return loading;
}

/**
 * True once `BlogRenderer` can render synchronously. Pages keep their loading
 * skeleton until this is true, so the article never commits empty and anything
 * that measures the rendered HTML (e.g. the table of contents) sees it.
 */
export function useBlogRendererReady(): boolean {
  const [ready, setReady] = useState(loaded !== null);
  useEffect(() => {
    if (ready) return;
    let alive = true;
    loadBlogHtml().then(
      () => { if (alive) setReady(true); },
      () => {},
    );
    return () => { alive = false; };
  }, [ready]);
  return ready;
}

export function BlogRenderer({ content, className }: BlogRendererProps) {
  const ready = useBlogRendererReady();
  const html = useMemo(
    () => (ready && loaded ? loaded.renderBlogHtml(content) : ""),
    [ready, content],
  );

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
      style={{
        /* Scoped prose styling for rendered content */
      }}
    />
  );
}
